import React from 'react';
import { formatCurrency } from '../utils/formatters';
import StatCard from './StatCard';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const AnalyticsView = ({ listings }) => {
  // Cálculos de negocio
  const totalListings = listings.length;
  
  const statusCounts = listings.reduce((acc, curr) => {
    const status = curr.status || 'Disponible';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = listings.reduce((acc, curr) => {
    acc[curr.property_type] = (acc[curr.property_type] || 0) + 1;
    return acc;
  }, {});

  const totalValueHNL = listings
    .filter(l => l.currency === 'HNL')
    .reduce((sum, l) => sum + Number(l.price), 0);

  const totalValueUSD = listings
    .filter(l => l.currency === 'USD')
    .reduce((sum, l) => sum + Number(l.price), 0);

  const getPercentage = (count) => ((count / totalListings) * 100).toFixed(0);

  return (
    <div className="space-y-8">
      {/* Grid de KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Propiedades" 
          value={totalListings} 
          icon={FiIcons.FiHome} 
          color="emerald"
          subtitle="Inventario total activo"
        />
        <StatCard 
          title="Disponibles" 
          value={statusCounts['Disponible'] || 0} 
          icon={FiIcons.FiCheckCircle} 
          color="blue"
          subtitle={`${getPercentage(statusCounts['Disponible'] || 0)}% del catálogo`}
        />
        <StatCard 
          title="Cartera HNL" 
          value={formatCurrency(totalValueHNL, 'HNL')} 
          icon={FiIcons.FiTrendingUp} 
          color="indigo"
          subtitle="Valor total en Lempiras"
        />
        <StatCard 
          title="Cartera USD" 
          value={formatCurrency(totalValueUSD, 'USD')} 
          icon={FiIcons.FiDollarSign} 
          color="amber"
          subtitle="Valor total en Dólares"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Distribución por Estado */}
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-2 h-6 bg-emerald-500 rounded-full"></div>
            Estado del Inventario
          </h3>
          <div className="space-y-6">
            {['Disponible', 'Vendido', 'Rentado'].map(status => {
              const count = statusCounts[status] || 0;
              const pct = totalListings > 0 ? (count / totalListings) * 100 : 0;
              const colors = {
                'Disponible': 'bg-emerald-500',
                'Vendido': 'bg-blue-500',
                'Rentado': 'bg-amber-500'
              };
              
              return (
                <div key={status}>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-sm font-bold text-gray-600 uppercase tracking-wider">{status}</span>
                    <span className="text-sm font-black text-gray-900">{count} unidades</span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      className={`h-full ${colors[status] || 'bg-gray-400'}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Distribución por Tipo de Propiedad */}
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-2 h-6 bg-indigo-500 rounded-full"></div>
            Tipos de Propiedad
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(typeCounts).map(([type, count]) => (
              <div key={type} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-[10px] font-black text-gray-400 uppercase mb-1">{type}</p>
                <p className="text-xl font-black text-gray-900">{count}</p>
                <div className="w-full h-1 bg-gray-200 mt-3 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-indigo-500" 
                    style={{ width: `${(count / totalListings) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
            {Object.keys(typeCounts).length === 0 && (
              <p className="col-span-2 text-center text-gray-400 text-sm py-10">Sin datos registrados</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;